import './OkulyonetimProductList.scss'

import {FilterOutlined} from '@ant-design/icons'
import {Button, Col, Collapse, Pagination, Row, Select, TreeDataNode} from 'antd'
import React, {useContext, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'

import AppContext from '../../../context/AppContext'
import {OkulYonetimUrunFiltre, useOkulyonetimrunlerQuery} from '../../../gql/GeneratedGraphqlTypes'
import useMediaQuery from '../../../hooks/useMediaQuery'
import Spinner from '../../spinner/Spinner'
import OkulyonetimAlert from '../okulyonetim-alert/OkulyonetimAlert'
import OkulyonetimCategoryFilter from '../okulyonetim-filters/okulyonetim-category-filter/OkulyonetimCategoryFilter'
import OkulyonetimDersFilter from '../okulyonetim-filters/okulyonetim-ders-filter/OkulyonetimDersFilter'
import OkulyonetimOtherFilter from '../okulyonetim-filters/okulyonetim-other-filter/OkulyonetimOtherFilter'
import OkulyonetimSinifFilter from '../okulyonetim-filters/okulyonetim-sinif-filter/OkulyonetimSinifFilter'
import OkulyonetimProduct from '../okulyonetim-product/OkulyonetimProduct'

export interface CustomTreeDataNode extends TreeDataNode {
    parentKey?: React.Key
}

const OkulyonetimProductList = () => {
    const {slug} = useContext(AppContext)
    const navigate = useNavigate()
    const isMobile = useMediaQuery(992)

    const getLastSlugPart = (): string | undefined => {
        const pathname = window.location.pathname
        const slugPart = pathname.split('/').pop()
        return slugPart
    }

    const lastSlugPart = getLastSlugPart()

    const [filters, setFilters] = useState<OkulYonetimUrunFiltre>({
        tip_slug: lastSlugPart || '',
        kategori_slug: [], // Kategori filtresi sıfırlanıyor
        yayinevi_slug: [], // Yayinevi filtresi sıfırlanıyor
        ders_slug: [], // Ders filtresi sıfırlanıyor
        sinif_slug: [], // Sınıf filtresi sıfırlanıyor
        sayfa: 0, // Sayfa sıfırlanıyor
        orderBy: 0, // Sayfa sıfırlanıyor
    })
    const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]) // Seçili kategorilerin anahtarları
    const [checkedYayineviKeys, setCheckedYayineviKeys] = useState<React.Key[]>([]) // Seçili yayınevlerinin anahtarları
    const [checkedSinifKeys, setCheckedSinifKeys] = useState<React.Key[]>([]) // Seçili sınıfların anahtarları
    const [checkedDersKeys, setCheckedDersKeys] = useState<React.Key[]>([]) // Seçili derslerin anahtarları
    const [currentPage, setCurrentPage] = useState(1)

    const {data, loading, error, refetch} = useOkulyonetimrunlerQuery({
        variables: {
            slug: slug!,
            filtre: filters,
        },
    })

    useEffect(() => {
        if (lastSlugPart) {
            setFilters({
                tip_slug: lastSlugPart, // Yeni tip_slug atanıyor
                kategori_slug: [], // Kategori filtresi sıfırlanıyor
                yayinevi_slug: [], // Yayinevi filtresi sıfırlanıyor
                ders_slug: [], // Ders filtresi sıfırlanıyor
                sinif_slug: [], // Sınıf filtresi sıfırlanıyor
                sayfa: 0, // Sayfa sıfırlanıyor
                orderBy: 0
            })
            setCurrentPage(1) // Sayfa numarası sıfırlanıyor
        }
    }, [lastSlugPart])

    useEffect(() => {
        if (filters) {
            refetch({filtre: filters})
        }
    }, [filters, refetch])

    useEffect(() => {
        if (error) {
            console.error(error)
            navigate('/')
        }
    }, [error, navigate])

    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        setFilters((prevFilters) => ({
            ...prevFilters,
            sayfa: page - 1,
        }))
    }
    const buildCategoryTree = (categories: any[]): CustomTreeDataNode[] => {
        const categoryMap: { [key: string]: CustomTreeDataNode } = {}
        const tree: CustomTreeDataNode[] = []

        // Kategorileri harita içine ekle
        categories.forEach((category) => {
            const node: CustomTreeDataNode = {
                key: category.kategori_slug,
                title: category.isim || 'Bilinmeyen Kategori',
                children: [],
            }
            categoryMap[category.kategori_slug] = node

            if (!category.parent_slug) {
                tree.push(node)
            }
        })

        // Alt kategorileri ebeveyn kategorilere bağla
        categories.forEach((category) => {
            if (category.alt_kategoriler && category.alt_kategoriler.length > 0) {
                category.alt_kategoriler.forEach((subCategory: any, index: number) => {
                    const uniqueKey = `${subCategory.alt_kategori_slug}*${index}*${category.kategori_slug}` // Benzersiz key
                    const childNode: CustomTreeDataNode = {
                        key: uniqueKey,
                        title: subCategory.isim,
                        parentKey: category.kategori_slug, // Üst kategorinin key'i
                        children: [],
                    }

                    // Alt kategoriyi ebeveynine bağla
                    if (categoryMap[category.kategori_slug]) {
                        categoryMap[category.kategori_slug]?.children?.push(childNode)
                    }
                })
            }
        })

        return tree
    }

    const formatTreeData = (): TreeDataNode[] => {
        const categories = [...(data?.okul?.okulyonetimUrunler?.kategoriler || [])] // `readonly` özelliğini kaldırır
        return buildCategoryTree(categories)
    }
    if (loading) {
        return <Spinner showSpinner={loading} color="red"/>
    }
    const isAnyFilterActive = () => {
        // Filtrelerden herhangi biri doluysa true döner
        return (
            filters?.kategori_slug?.length! > 0 ||
            filters?.alt_kategori_slug?.length! > 0 ||
            filters?.yayinevi_slug?.length! > 0 ||
            filters?.ders_slug?.length! > 0 ||
            filters?.sinif_slug?.length! > 0
        )
    }

    function getSelectValueFromOrderBy(orderBy: number): string {
        switch (orderBy) {
            case 1: return 'fiyatArtan'
            case 2: return 'fiyatAzalan'
            case 3: return 'isimArtan'
            case 4: return 'isimAzalan'
            default: return 'onerilen'
        }
    }
    return (
        <div className="okulyonetim-product-list">
            <OkulyonetimAlert/>
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={6}>

                    {isMobile ? (
                        <div className="okulyonetim-product-list-filter">
                            <Collapse
                                size="large"
                                expandIcon={() => (
                                    <div className="okulyonetim-product-list-filter-icon">
                                        <FilterOutlined/>
                                    </div>
                                )}
                            >
                                <Collapse.Panel key={1} header={'Filtrele'}>
                                    <OkulyonetimCategoryFilter
                                        treeData={formatTreeData() as CustomTreeDataNode[]}
                                        checkedKeys={checkedKeys} // Seçili kategoriler
                                        onCheck={(updatedCheckedKeys, info) => {
                                            // Mevcut kategori ve alt kategori sluglarını koru
                                            let kategoriSluglar: string[] = Array.isArray(filters?.kategori_slug)
                                                ? [...(filters.kategori_slug as string[])]
                                                : []
                                            let altKategoriSluglar: string[] = Array.isArray(filters?.alt_kategori_slug)
                                                ? [...(filters.alt_kategori_slug as string[])]
                                                : []

                                            if (info.checked) {
                                                // Eğer filtre ekleniyorsa
                                                if (info.node.children && info.node.children.length > 0) {
                                                    // Ana kategori seçilmişse
                                                    const selectedKategoriSluglar = updatedCheckedKeys.filter(
                                                        (key) => key === info.node.key,
                                                    ) as string[]

                                                    kategoriSluglar = Array.from(new Set([...kategoriSluglar, ...selectedKategoriSluglar]))

                                                    const childKeys = info.node.children.map((child: any) => {
                                                        const key = child.key.split('*')[0]
                                                        return key
                                                    })

                                                    altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, ...childKeys]))
                                                } else {
                                                    // Alt kategori seçilmişse
                                                    const selectedAltKategoriSluglar = updatedCheckedKeys.map((key) => {
                                                        if (typeof key === 'string') {
                                                            const splitKey = key.split('*')[0]
                                                            return splitKey
                                                        }
                                                        return ''
                                                    })

                                                    altKategoriSluglar = Array.from(
                                                        new Set([...altKategoriSluglar, ...selectedAltKategoriSluglar]),
                                                    )

                                                    if (info.node.parentKey) {
                                                        kategoriSluglar = Array.from(new Set([...kategoriSluglar, info.node.parentKey]))
                                                    }
                                                }
                                            } else {
                                                // Eğer filtre kaldırılıyorsa
                                                if (info.node.children && info.node.children.length > 0) {
                                                    // Ana kategori kaldırılmışsa
                                                    kategoriSluglar = kategoriSluglar.filter((slug) => slug !== info.node.key)

                                                    const childKeys = info.node.children.map((child: any) => {
                                                        const key = child.key.split('*')[0]
                                                        return key
                                                    })

                                                    altKategoriSluglar = altKategoriSluglar.filter((slug) => !childKeys.includes(slug))
                                                } else {
                                                    // Alt kategori kaldırılmışsa
                                                    const removedAltKategoriSlug = info.node.key.split('*')[0]
                                                    altKategoriSluglar = altKategoriSluglar.filter((slug) => slug !== removedAltKategoriSlug)

                                                    if (info.node.parentKey) {
                                                        const parentSlug = info.node.parentKey

                                                        // Bu parent'a ait başka alt kategori hala seçili mi?
                                                        const otherAltKategorilerVarMi = updatedCheckedKeys.some((key) => {
                                                            if (typeof key === 'string') {
                                                                const [altSlug, , altParent] = key.split('*')
                                                                return altParent === parentSlug && altSlug !== info.node.key.split('*')[0]
                                                            }
                                                            return false
                                                        })

                                                        // Eğer başka alt kategori yoksa, parent'ı da sil
                                                        if (!otherAltKategorilerVarMi) {
                                                            kategoriSluglar = kategoriSluglar.filter((slug) => slug !== parentSlug)
                                                        }
                                                    }

                                                }
                                            }

                                            // Filtreleri güncelle
                                            setCurrentPage(1)
                                            setFilters((prevFilters) => ({
                                                ...prevFilters,
                                                kategori_slug: Array.from(new Set([...kategoriSluglar])),
                                                alt_kategori_slug: Array.from(new Set([...altKategoriSluglar])),
                                                sayfa: 0,
                                            }))

                                            setCheckedKeys(updatedCheckedKeys)
                                        }}
                                    />
                                    <OkulyonetimOtherFilter
                                        title="Yayınevleri"
                                        searchbar
                                        checkedTempKeys={checkedYayineviKeys.map((key) => key.toString())}
                                        options={
                                            data?.okul?.okulyonetimUrunler?.yayinevleri
                                                ?.filter((yayinevi) => yayinevi !== null && yayinevi?.yayinevi_count > 0)
                                                ?.map((yayinevi) => ({
                                                    title: yayinevi?.isim || 'Diğerleri',
                                                    count: yayinevi?.yayinevi_count,
                                                    value: yayinevi?.yayinevi_slug || '', // Null değerlerden kaçının
                                                    isSelected: yayinevi?.isSelected,
                                                })) || []
                                        }
                                        onChange={(selectedYayineviSlugs) => {
                                            setCurrentPage(1)
                                            setFilters((prevFilters) => {
                                                // Mevcut slug değerlerini al
                                                const currentSlugs = prevFilters.yayinevi_slug || []

                                                // Gelen slug değerlerini kontrol et ve güncelle
                                                const updatedSlugs = selectedYayineviSlugs.reduce((acc, slug) => {
                                                    if (currentSlugs.includes(slug)) {
                                                        // Eğer mevcutta varsa, çıkar
                                                        return acc.filter((existingSlug) => existingSlug !== slug)
                                                    } else {
                                                        // Eğer mevcutta yoksa, ekle
                                                        return [...acc, slug]
                                                    }
                                                }, currentSlugs)
                                                setCheckedYayineviKeys(updatedSlugs as React.Key[])
                                                return {
                                                    ...prevFilters,
                                                    yayinevi_slug: updatedSlugs,
                                                    sayfa: 0,
                                                }
                                            })
                                        }}
                                    />
                                    <OkulyonetimSinifFilter
                                        title="Sınıf Seviyesi"
                                        searchbar={false}
                                        checkedTempKeys={checkedSinifKeys.map((key) => key.toString())}
                                        options={
                                            data?.okul?.okulyonetimUrunler?.siniflar
                                                ?.filter((sinif) => sinif !== null && sinif?.sinif_count > 0)
                                                ?.map((sinif) => ({
                                                    title: sinif?.isim || 'Diğerleri',
                                                    count: sinif?.sinif_count,
                                                    value: sinif?.sinif_slug || '', // Null değerlerden kaçının
                                                    isSelected: false,
                                                })) || []
                                        }
                                        onChange={(selectedSinifSlugs) => {
                                            setCurrentPage(1)
                                            setFilters((prevFilters) => {
                                                // Mevcut slug değerlerini al
                                                const currentSlugs = prevFilters.yayinevi_slug || []

                                                // Gelen slug değerlerini kontrol et ve güncelle
                                                const updatedSlugs = selectedSinifSlugs.reduce((acc, slug) => {
                                                    if (currentSlugs.includes(slug)) {
                                                        // Eğer mevcutta varsa, çıkar
                                                        return acc.filter((existingSlug) => existingSlug !== slug)
                                                    } else {
                                                        // Eğer mevcutta yoksa, ekle
                                                        return [...acc, slug]
                                                    }
                                                }, currentSlugs)
                                                setCheckedSinifKeys(updatedSlugs as React.Key[])
                                                return {
                                                    ...prevFilters,
                                                    sinif_slug: updatedSlugs,
                                                    sayfa: 0,
                                                }
                                            })
                                        }}
                                    />
                                    <OkulyonetimDersFilter
                                        title="Ders"
                                        searchbar={false}
                                        checkedTempKeys={checkedDersKeys.map((key) => key.toString())}
                                        options={
                                            data?.okul?.okulyonetimUrunler?.dersler
                                                ?.filter((ders) => ders !== null && ders?.ders_count > 0)
                                                ?.map((ders) => ({
                                                    title: ders?.isim || 'Diğerleri',
                                                    count: ders?.ders_count,
                                                    value: ders?.ders_slug || '', // Null değerlerden kaçının
                                                    isSelected: false,
                                                })) || []
                                        }
                                        onChange={(selectedDersSlugs) => {
                                            setCurrentPage(1)
                                            setFilters((prevFilters) => {
                                                // Mevcut slug değerlerini al
                                                const currentSlugs = prevFilters.yayinevi_slug || []

                                                // Gelen slug değerlerini kontrol et ve güncelle
                                                const updatedSlugs = selectedDersSlugs.reduce((acc, slug) => {
                                                    if (currentSlugs.includes(slug)) {
                                                        // Eğer mevcutta varsa, çıkar
                                                        return acc.filter((existingSlug) => existingSlug !== slug)
                                                    } else {
                                                        // Eğer mevcutta yoksa, ekle
                                                        return [...acc, slug]
                                                    }
                                                }, currentSlugs)
                                                setCheckedDersKeys(updatedSlugs as React.Key[])
                                                return {
                                                    ...prevFilters,
                                                    ders_slug: updatedSlugs,
                                                    sayfa: 0,
                                                }
                                            })
                                        }}
                                    />
                                </Collapse.Panel>
                            </Collapse>
                        </div>
                    ) : (
                        <div>
                            <OkulyonetimCategoryFilter
                                treeData={formatTreeData() as CustomTreeDataNode[]}
                                checkedKeys={checkedKeys} // Seçili kategoriler
                                onCheck={(updatedCheckedKeys, info) => {
                                    // Mevcut kategori ve alt kategori sluglarını koru
                                    let kategoriSluglar: string[] = Array.isArray(filters?.kategori_slug)
                                        ? [...(filters.kategori_slug as string[])]
                                        : []
                                    let altKategoriSluglar: string[] = Array.isArray(filters?.alt_kategori_slug)
                                        ? [...(filters.alt_kategori_slug as string[])]
                                        : []
                                    if (info.checked) {
                                        // Eğer filtre ekleniyorsa
                                        if (info.node.children && info.node.children.length > 0) {
                                            // Ana kategori seçilmişse
                                            const selectedKategoriSluglar = updatedCheckedKeys.filter(
                                                (key) => key === info.node.key,
                                            ) as string[]

                                            kategoriSluglar = Array.from(new Set([...kategoriSluglar, ...selectedKategoriSluglar]))

                                            const childKeys = info.node.children.map((child: any) => {
                                                const key = child.key.split('*')[0]
                                                return key
                                            })

                                            altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, ...childKeys]))
                                        } else {
                                            // Alt kategori seçilmişse
                                            const selectedAltKategoriSluglar = updatedCheckedKeys.map((key) => {
                                                if (typeof key === 'string') {
                                                    const splitKey = key.split('*')[0]
                                                    return splitKey
                                                }
                                                return ''
                                            })

                                            altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, ...selectedAltKategoriSluglar]))

                                            if (info.node.parentKey) {
                                                kategoriSluglar = Array.from(new Set([...kategoriSluglar, info.node.parentKey]))
                                            }
                                        }
                                    } else {
                                        // Eğer filtre kaldırılıyorsa
                                        if (info.node.children && info.node.children.length > 0) {
                                            // Ana kategori kaldırılmışsa
                                            kategoriSluglar = kategoriSluglar.filter((slug) => slug !== info.node.key)

                                            const childKeys = info.node.children.map((child: any) => {
                                                const key = child.key.split('*')[0]
                                                return key
                                            })

                                            altKategoriSluglar = altKategoriSluglar.filter((slug) => !childKeys.includes(slug))
                                        } else {
                                            // Alt kategori kaldırılmışsa
                                            const removedAltKategoriSlug = info.node.key.split('*')[0]
                                            altKategoriSluglar = altKategoriSluglar.filter((slug) => slug !== removedAltKategoriSlug)

                                            if (info.node.parentKey) {
                                                const parentSlug = info.node.parentKey
                                                const isParentStillSelected = updatedCheckedKeys.includes(parentSlug)
                                                if (!isParentStillSelected) {
                                                    kategoriSluglar = kategoriSluglar.filter((slug) => slug !== parentSlug)
                                                }
                                            }
                                        }
                                    }

                                    // Filtreleri güncelle
                                    setCurrentPage(1)
                                    setFilters((prevFilters) => ({
                                        ...prevFilters,
                                        kategori_slug: Array.from(new Set([...kategoriSluglar])),
                                        alt_kategori_slug: Array.from(new Set([...altKategoriSluglar])),
                                        sayfa: 0,
                                    }))

                                    setCheckedKeys(updatedCheckedKeys)
                                }}
                            />

                            <OkulyonetimOtherFilter
                                title="Yayınevleri"
                                searchbar
                                checkedTempKeys={checkedYayineviKeys.map((key) => key.toString())}
                                options={
                                    data?.okul?.okulyonetimUrunler?.yayinevleri
                                        ?.filter((yayinevi) => yayinevi !== null && yayinevi?.yayinevi_count > 0)
                                        ?.map((yayinevi) => ({
                                            title: yayinevi?.isim || 'Diğerleri',
                                            count: yayinevi?.yayinevi_count,
                                            value: yayinevi?.yayinevi_slug || '', // Null değerlerden kaçının
                                            isSelected: yayinevi?.isSelected,
                                        })) || []
                                }
                                onChange={(selectedYayineviSlugs) => {
                                    setCurrentPage(1)
                                    setFilters((prevFilters) => {
                                        // Mevcut slug değerlerini al
                                        const currentSlugs = prevFilters.yayinevi_slug || []

                                        // Gelen slug değerlerini kontrol et ve güncelle
                                        const updatedSlugs = selectedYayineviSlugs.reduce((acc, slug) => {
                                            if (currentSlugs.includes(slug)) {
                                                // Eğer mevcutta varsa, çıkar
                                                return acc.filter((existingSlug) => existingSlug !== slug)
                                            } else {
                                                // Eğer mevcutta yoksa, ekle
                                                return [...acc, slug]
                                            }
                                        }, currentSlugs)
                                        setCheckedYayineviKeys(updatedSlugs as React.Key[])
                                        return {
                                            ...prevFilters,
                                            yayinevi_slug: updatedSlugs,
                                            sayfa: 0,
                                        }
                                    })
                                }}
                            />
                            <OkulyonetimOtherFilter
                                title="Sınıf Seviyesi"
                                searchbar={false}
                                checkedTempKeys={checkedSinifKeys.map((key) => key.toString())}
                                options={
                                    data?.okul?.okulyonetimUrunler?.siniflar
                                        ?.filter((sinif) => sinif !== null && sinif?.sinif_count > 0)
                                        ?.map((sinif) => ({
                                            title: sinif?.isim || 'Diğerleri',
                                            count: sinif?.sinif_count,
                                            value: sinif?.sinif_slug || '', // Null değerlerden kaçının
                                            isSelected: false,
                                        })) || []
                                }
                                onChange={(selectedSinifSlugs) => {
                                    setCurrentPage(1)
                                    setFilters((prevFilters) => {
                                        // Mevcut slug değerlerini al
                                        const currentSlugs = prevFilters.sinif_slug || []

                                        // Gelen slug değerlerini kontrol et ve güncelle
                                        const updatedSlugs = selectedSinifSlugs.reduce((acc, slug) => {
                                            if (currentSlugs.includes(slug)) {
                                                // Eğer mevcutta varsa, çıkar
                                                return acc.filter((existingSlug) => existingSlug !== slug)
                                            } else {
                                                // Eğer mevcutta yoksa, ekle
                                                return [...acc, slug]
                                            }
                                        }, currentSlugs)
                                        setCheckedSinifKeys(updatedSlugs as React.Key[])
                                        return {
                                            ...prevFilters,
                                            sinif_slug: updatedSlugs,
                                            sayfa: 0,
                                        }
                                    })
                                }}
                            />
                            <OkulyonetimOtherFilter
                                title="Ders"
                                searchbar={false}
                                checkedTempKeys={checkedDersKeys.map((key) => key.toString())}
                                options={
                                    data?.okul?.okulyonetimUrunler?.dersler
                                        ?.filter((ders) => ders !== null && ders?.ders_count > 0)
                                        ?.map((ders) => ({
                                            title: ders?.isim || 'Diğerleri',
                                            count: ders?.ders_count,
                                            value: ders?.ders_slug || '', // Null değerlerden kaçının
                                            isSelected: false,
                                        })) || []
                                }
                                onChange={(selectedDersSlugs) => {
                                    setCurrentPage(1)
                                    setFilters((prevFilters) => {
                                        // Mevcut slug değerlerini al
                                        const currentSlugs = prevFilters.ders_slug || []

                                        // Gelen slug değerlerini kontrol et ve güncelle
                                        const updatedSlugs = selectedDersSlugs.reduce((acc, slug) => {
                                            if (currentSlugs.includes(slug)) {
                                                // Eğer mevcutta varsa, çıkar
                                                return acc.filter((existingSlug) => existingSlug !== slug)
                                            } else {
                                                // Eğer mevcutta yoksa, ekle
                                                return [...acc, slug]
                                            }
                                        }, currentSlugs)
                                        setCheckedDersKeys(updatedSlugs as React.Key[])
                                        return {
                                            ...prevFilters,
                                            ders_slug: updatedSlugs,
                                            sayfa: 0,
                                        }
                                    })
                                }}
                            />
                        </div>
                    )}
                </Col>
                <Col xs={24} lg={18}>
                    <Row gutter={[16, 16]} className="okulyonetim-product-list-top">
                        {/* Seçtikleriniz */}
                        <Col xs={24} md={18}>
                            {isAnyFilterActive() && (
                                <div className="okulyonetim-selected-filters">
                                    <div className="filters-header">
                                        <span className="label">Seçtikleriniz:</span>
                                        <Button
                                            danger
                                            className="clear-filters-button"
                                            onClick={() => {
                                                // Tüm filtreleri temizle
                                                setFilters({
                                                    tip_slug: lastSlugPart || '',
                                                    kategori_slug: [],
                                                    yayinevi_slug: [],
                                                    ders_slug: [],
                                                    sinif_slug: [],
                                                    sayfa: 0,
                                                    orderBy: 0
                                                })
                                                setCheckedKeys([])
                                                setCheckedYayineviKeys([])
                                                setCheckedSinifKeys([])
                                                setCheckedDersKeys([])
                                                setCurrentPage(1)
                                            }}
                                        >
                                            Filtreleri Temizle
                                        </Button>

                                    </div>
                                    <div className="okulyonetim-selected-filters-head">
                                        {/* 1) Kategoriler */}
                                        {filters.kategori_slug?.map((slug) => (
                                            <span key={`kat-${slug}`} className="selected-filter-chip">{slug}
                                                <button
                                                    onClick={() => {
                                                        // Ana kategoriyi kaldır
                                                        const newKategoriSlugs = filters.kategori_slug?.filter((item) => item !== slug) || [];
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            kategori_slug: newKategoriSlugs,
                                                            alt_kategori_slug: [],
                                                            sayfa: 0,
                                                        }));
                                                        // **Tree'deki check durumunu tamamen kaldır**
                                                        setCheckedKeys([]);
                                                    }}
                                                >
                                                    x
                                                </button>
                                            </span>
                                            ))}
                                        {/* 2) Alt Kategoriler */}
                                        {filters.alt_kategori_slug?.map((slug) => (
                                            <span key={`altkat-${slug}`} className="selected-filter-chip">{slug}
                                                <button
                                                    onClick={() => {
                                                        const newAltKategoriSlugs =
                                                            filters.alt_kategori_slug?.filter((item) => item !== slug) || []

                                                        if (newAltKategoriSlugs.length > 0){
                                                            setFilters((prev) => ({
                                                                ...prev,
                                                                alt_kategori_slug: newAltKategoriSlugs,
                                                                sayfa: 0,
                                                            }))
                                                            // Ağaçta da uncheck olsun isterseniz (uniqueKey’e dikkat!):
                                                            setCheckedKeys((prev) => prev.filter((k) => {
                                                                // Kayıtlar "alt_kategori_slug*index*parentSlug" şeklinde tutulabilir
                                                                // Dolayısıyla .split('*')[0] ile eşleşme kontrolü yapabilirsiniz.
                                                                const realSlug = typeof k === 'string' ? k.split('*')[0] : k
                                                                return realSlug !== slug
                                                            }))
                                                        } else {
                                                            setFilters((prev) => ({
                                                                ...prev,
                                                                alt_kategori_slug: newAltKategoriSlugs,
                                                                kategori_slug:[],
                                                                sayfa: 0,
                                                            }))
                                                            setCheckedKeys([]);
                                                        }
                                                       
                                                    }}
                                                >x</button></span>
                                        ))}

                                        {/* 3) Dersler */}
                                        {filters.ders_slug?.map((slug) => (
                                            <span key={`ders-${slug}`} className="selected-filter-chip">{slug}
                                                <button
                                                    onClick={() => {
                                                        const newDersSlugs = filters.ders_slug?.filter((item) => item !== slug) || []
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            ders_slug: newDersSlugs,
                                                            sayfa: 0,
                                                        }))
                                                        setCheckedDersKeys((prev) => prev.filter((k) => k !== slug))
                                                    }}>x</button></span>
                                        ))}

                                        {/* 4) Sınıflar */}
                                        {filters.sinif_slug?.map((slug) => (
                                            <span key={`sinif-${slug}`} className="selected-filter-chip">{slug}
                                                <button
                                                    onClick={() => {
                                                        const newSinifSlugs = filters.sinif_slug?.filter((item) => item !== slug) || []
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            sinif_slug: newSinifSlugs,
                                                            sayfa: 0,
                                                        }))
                                                        setCheckedSinifKeys((prev) => prev.filter((k) => k !== slug))
                                                    }}>x</button></span>
                                        ))}

                                        {/* 5) Yayınevleri */}
                                        {filters.yayinevi_slug?.map((slug) => (
                                            <span key={`yay-${slug}`} className="selected-filter-chip">{slug}
                                                <button
                                                    onClick={() => {
                                                        const newYayineviSlugs = filters.yayinevi_slug?.filter((item) => item !== slug) || []
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            yayinevi_slug: newYayineviSlugs,
                                                            sayfa: 0,
                                                        }))
                                                        setCheckedYayineviKeys((prev) => prev.filter((k) => k !== slug))
                                                    }}>x</button></span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </Col>
                        {/* Sıralama vs. (Sizin mevcut kodunuz) */}
                        <Col xs={24} md={6}>
                            <div className="okulyonetim-sorting-view">
                                <span className="label">Sıralama: </span>
                                <Select
                                    value={getSelectValueFromOrderBy(filters.orderBy!)}
                                    onChange={(value) => {
                                        const selectedValue = value
                                        let newOrderBy = 0
                                        switch (selectedValue) {
                                            case 'fiyatArtan':
                                                newOrderBy = 1
                                                break
                                            case 'fiyatAzalan':
                                                newOrderBy = 2
                                                break
                                            case 'isimArtan':
                                                newOrderBy = 3
                                                break
                                            case 'isimAzalan':
                                                newOrderBy = 4
                                                break
                                            default:
                                                newOrderBy = 0
                                        }

                                        setFilters((prev) => ({
                                            ...prev,
                                            orderBy: newOrderBy,
                                            sayfa: 0,
                                        }))

                                        // sayfayı sıfırlıyorsanız
                                        setCurrentPage(1)
                                    }}
                                >
                                    <option value="onerilen"> Önerilen</option>
                                    <option value="fiyatArtan">Fiyata Göre Artan</option>
                                    <option value="fiyatAzalan">Fiyata Göre Azalan</option>
                                    <option value="isimArtan"> A'dan Z'ye</option>
                                    <option value="isimAzalan"> Z'den A'ya</option>
                                </Select>
                            </div>
                        </Col>
                    </Row>
                    <Row className="okulyonetim-product-list-row" gutter={[24, 24]} wrap={true}>
                        {data?.okul?.okulyonetimUrunler?.okulyonetim_urunler?.map((product, index) => (
                            <Col xs={24} sm={12} md={8} lg={6} key={index}>
                                <OkulyonetimProduct
                                    image={product?.urun?.resim ? product.urun.resim : product?.urun?.resim_url!}
                                    title={product?.urun?.isim!}
                                    pdf_url={product?.urun?.pdf_url}
                                    pdf={product?.urun?.pdf}
                                    id={product?.urun?.id ? product.urun.id : 1}
                                    price={product?.urun?.etiket_fiyati!}
                                    quantity={1}
                                    mufredat={product?.urun?.mufredat ? product.urun.mufredat : false}
                                    oneri={product?.urun?.oneri ? product.urun.oneri : false}
                                    initialIsFavori={product?.urun?.is_favori ? product.urun.is_favori : false}
                                />
                            </Col>
                        ))}
                    </Row>
                </Col>
            </Row>
            <Pagination
                current={currentPage}
                pageSize={20}
                total={data?.okul?.okulyonetimUrunler?.toplam_urun_sayisi!}
                onChange={handlePageChange}
                style={{marginTop: 16, textAlign: 'center'}}
                showSizeChanger={false}
            />
        </div>
    )
}

export default OkulyonetimProductList
